import type {
  LinksFunction,
  LoaderFunctionArgs,
  MetaArgs,
} from "@remix-run/cloudflare";
import {
  isRouteErrorResponse,
  json,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteError,
  useRouteLoaderData,
} from "@remix-run/react";
import { captureRemixErrorBoundaryError } from "@sentry/remix";
import { withSentry } from "@sentry/remix";

import "./styles/global.css";
import "./styles/reset.css";
import "./styles/variables.css";

import { constructionTheme } from "./styles/construction.css";
import { nationalTheme } from "./styles/national.css";
import { energyTheme } from "./styles/energy.css";
import { manufacturingTheme } from "./styles/manufacturing.css";
import { miningTheme } from "./styles/mining.css";

import { VisualEditing } from "hydrogen-sanity/visual-editing";

import { queryFooter, queryHeader } from "./queries/menu";
import {
  Menu as IMenu,
  Settings as ISettings,
  Microcopy as IMicrocopy,
} from "./sanity/types";
import { queryMicrocopy, querySettings } from "./queries/globals";
import { Layout as BaseLayout } from "./components/globals/Layout";
import { getSeoMeta, SeoConfig, useNonce } from "@shopify/hydrogen";
import { urlFor } from "./components/elements/Image";
import TemplatePageNotFound from "./components/templates/TemplatePageNotFound";
import TemplatePageError from "./components/templates/TemplatePageError";
import { removeTrailingSlashes } from "./lib/remove-trailing-slashes";
import { northAmericaTheme } from "./styles/northamerica.css";
import { getGravityForm } from "next-gravity-forms/server";

export const meta = ({ data }: MetaArgs<typeof loader>) => {
  return getSeoMeta(data?.seo as SeoConfig);
};

export const links: LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap",
  },
];

export async function loader({ context, request }: LoaderFunctionArgs) {
  removeTrailingSlashes({ request });

  const url = new URL(request.url);
  const siteTheme = context.cloudflare.env.SITE_THEME || "Construction";
  const preview = context.sanity.preview?.enabled;
  const footer = await context.sanity.loadQuery<IMenu>(queryFooter);
  const header = await context.sanity.loadQuery<IMenu>(queryHeader);
  const settings = await context.sanity.loadQuery<ISettings>(querySettings);
  const microcopy = await context.sanity.loadQuery<IMicrocopy[]>(
    queryMicrocopy,
  );
  const sanityProjectID = context.cloudflare.env.SANITY_PROJECT_ID;
  const sanityDataset = context.cloudflare.env.SANITY_DATASET;
  const sanityPreview = context.sanity.preview?.enabled;
  const seoCanonical = url.origin + url.pathname;
  const seoImage = settings?.data?.seo?.image?.asset;
  const seo = {
    ...settings?.data?.seo,
    media:
      seoImage &&
      urlFor({
        source: seoImage,
        sanityDataset: sanityDataset,
        sanityProjectID: sanityProjectID,
      })
        .width(1200)
        .height(630)
        .url(),
    url: seoCanonical,
  };
  const searchParams = url.searchParams;
  const gtmDisable = searchParams.get("gtmDisable");
  const gtmContainerId = context.cloudflare.env.GOOGLE_TAG_MANAGER_CONTAINER_ID;

  let mailingListForm;
  if (settings?.data?.mailingList?.formId) {
    mailingListForm = await getGravityForm(
      settings?.data?.mailingList?.formId,
      context.cloudflare.env.WORDPRESS_API_URL,
    );
  }

  return json({
    siteTheme,
    preview,
    footer,
    header,
    settings,
    mailingListForm: {
      data: mailingListForm,
      baseUrl: context.cloudflare.env.WORDPRESS_API_URL,
    },
    microcopy,
    seo,
    sanityDataset,
    sanityProjectID,
    sanityPreview,
    gtmContainerId,
    gtmDisable,
  });
}

export function Layout({ children }: { children: React.ReactNode }) {
  const nonce = useNonce();
  const data = useRouteLoaderData<typeof loader>("root");
  const siteTheme = data?.siteTheme as keyof typeof themes;
  const themes = {
    Construction: constructionTheme,
    National: nationalTheme,
    Mining: miningTheme,
    Energy: energyTheme,
    Manufacturing: manufacturingTheme,
    NorthAmerica: northAmericaTheme,
  };

  const favicons = {
    Construction: "/favicons/construction.ico",
    National: "/favicons/national.ico",
    Mining: "/favicons/mining.ico",
    Energy: "/favicons/energy.ico",
    Manufacturing: "/favicons/manufacturing.ico",
    NorthAmerica: "/favicons/construction.ico",
  };

  const faviconHref = favicons[siteTheme] || "/favicon.ico";

  const preview = data?.preview;

  const gtmDisable = data?.gtmDisable;
  const gtmContainerId = data?.gtmContainerId;
  const hasGtm = !gtmDisable && gtmContainerId;

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="format-detection" content="telephone=no" />
        <link rel="icon" href={faviconHref} />
        <Meta />
        <Links />
        {hasGtm && (
          <script
            nonce={nonce}
            dangerouslySetInnerHTML={{
              __html: `
                (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                })(window,document,'script','dataLayer','${gtmContainerId}');
              `,
            }}
            suppressHydrationWarning
          />
        )}
      </head>
      <body className={themes[siteTheme]}>
        {hasGtm && (
          <noscript
            dangerouslySetInnerHTML={{
              __html: `
              <iframe src="https://www.googletagmanager.com/ns.html?id=${gtmContainerId}"
              height="0" width="0" style="display:none;visibility:hidden"></iframe>
            `,
            }}
          />
        )}
        <BaseLayout>{children}</BaseLayout>
        {preview ? <VisualEditing /> : null}
        <ScrollRestoration nonce={nonce} />
        <Scripts nonce={nonce} />
      </body>
    </html>
  );
}

function App() {
  return <Outlet />;
}

export default withSentry(App);

export function ErrorBoundary() {
  const error = useRouteError();
  captureRemixErrorBoundaryError(error);
  const data = useRouteLoaderData<typeof loader>("root");
  const sanityPreviewEnabled = data?.sanityPreview;

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      const pageData = JSON.parse(error.data);
      const template = pageData?.template;

      if (template && template._type === "templatePageNotFound") {
        return <TemplatePageNotFound data={template} />;
      } else {
        return <h1>An Error Occured</h1>;
      }
    } else if (error.status === 503 && sanityPreviewEnabled) {
      return (
        <TemplatePageError
          heading="Under Construction"
          text=" Whoops, this page is still under construction and missing a
                        few bits of content in order to be previewed. Please continue
                        adding all required content in the desk structure before
                        attempting to preview."
        />
      );
    } else {
      return (
        <div>
          <h1>
            {error.status} {error.statusText}
          </h1>
          <p>{error.data}</p>
        </div>
      );
    }
  } else if (error instanceof Error) {
    return (
      // <div>
      //   <h1>Error</h1>
      //   <p>{error.message}</p>
      //   <p>The stack trace is:</p>
      //   <pre>{error.stack}</pre>
      // </div>
      <TemplatePageError
        text={`Looks like something went wrong. ${error.message}`}
      />
    );
  } else {
    return <TemplatePageError />;
    // <h1>Unknown Error</h1>;
  }
}
